/**
 * HTTP Step Executor — External HTTP/webhook calls as a workflow step
 *
 * Config:
 * - url: string
 * - method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
 * - headers?: Record<string, string>
 * - body?: unknown (JSON serializable)
 * - responseType?: 'json' | 'text'
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';

export const httpStepExecutor: StepExecutor = {
  type: 'http',
  async execute(step: EngineStep): Promise<StepExecutionResult> {
    try {
      const url = step.config.url as string;
      if (!url) {
        return { success: false, error: 'url is required for http step' };
      }

      const method = ((step.config.method as string) || 'GET').toUpperCase();
      const headers = (step.config.headers as Record<string, string>) || {};
      const body = step.config.body;
      const responseType = (step.config.responseType as string) || 'json';

      const fetchOptions: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      };

      if (body && method !== 'GET' && method !== 'HEAD') {
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(url, fetchOptions);
      const statusOk = response.ok;
      const status = response.status;

      let responseData: unknown;
      if (responseType === 'json') {
        try {
          responseData = await response.json();
        } catch {
          responseData = await response.text();
        }
      } else {
        responseData = await response.text();
      }

      if (!statusOk) {
        return {
          success: false,
          error: `HTTP ${status}: ${typeof responseData === 'string' ? responseData : JSON.stringify(responseData)}`,
          output: { httpStatus: status, httpResponse: responseData },
        };
      }

      return {
        success: true,
        output: { httpStatus: status, httpResponse: responseData },
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'HTTP request failed',
      };
    }
  },
};
